import React, { useContext, FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';

// MUI
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

// Custom Components
import useFormController from '@/components/useFormController';
import DynamicDialog from '@/ui/Dialog/DynamicDialog';
import DialogToolbarHeading from '@/ui/Dialog/DialogToolbarHeading';
import DialogToolbarButtonClose from '@/ui/Dialog/ToolbarButtons/DialogToolbarButtonClose';
import ToolbarFillContent from '@/ui/Toolbar/ToolbarFillContent';
import DialogActionButtonClose from '@/ui/Dialog/ActionButtons/DialogActionButtonClose';
import DialogActionButtonAdd from '@/ui/Dialog/ActionButtons/DialogActionButtonAdd';
import DialogActionButtonSave from '@/ui/Dialog/ActionButtons/DialogActionButtonSave';
import DialogContext, { IDialogProps } from '@/context/DialogContext/DialogContext';
import SnackbarContext from '@/context/SnackbarContext/SnackbarContext';
import { GridContainer } from '@/ui/Grid';
import FormContent from '@/components/FormContent';
import DialogToolbarMenuItemDeactivate from "./ToolbarMenuItems/DialogToolbarMenuItemDeactivate";
import DialogToolbarMenuItemActivate from "./ToolbarMenuItems/DialogToolbarMenuItemActivate";
import DialogToolbarMenuButton from "./DialogToolbarMenuButton";
import DialogInfoDeactivated from './DialogInfoDeactivated';
import DialogInfoMeta from './DialogInfoMeta';

// Types
import {
  ClosingDetails,
  FormControllerProps,
} from '@/@types/components/formController';


const GeneralDialog: FunctionComponent<IDialogProps> = (props) => {
  const { hideDialog } = useContext(DialogContext);
  const { showNotification } = useContext(SnackbarContext);
  const { t } = useTranslation();

  const { dc, mode, form, onClose, title, level, columns = 2, allowPrimaryModelActivate = false, geometryChangeType, mapId } = props;

  const {
    record,
    validation,
    fields,
    pickerDataControllers,
    onFieldChange,
    doInsert,
    doUpdate,
    doClose,
    doRefresh,
    doActivate,
    doDeactivate
  } = useFormController(props as FormControllerProps);

  const handleFieldChange = (value: any, source: string) => {
    if (onFieldChange) {
      onFieldChange(value, source);
    }
  };

  const handleInsert = () => {
    if (doInsert) {
      doInsert()
        .then((result) => {
          if (result.success) {
            close({ dataChanged: true, action: 'insert' });
          } else if (result.error) {
            const msg = `${t('error.addRecord')} ${result.error}`;
            showNotification(msg, 'error');
          } else if (result.validationPass === false) {
            // do nothing
          }
        })
        .catch((resp) => {
          showNotification(resp.error, 'error');
        });
    }
  };

  const handleUpdate = () => {
    if (doUpdate) {
      doUpdate()
        .then((result) => {
          if (result.success) {
            close({ dataChanged: true, action: 'update' });
          } else if (result.error) {
            const msg = `${t('error.editRecord')  } ${  result.error}`;
            showNotification(msg, 'error');
          } else if (result.validationPass === false) {
            // do nothing
          }
        })
        .catch((resp) => {
          showNotification(resp.error, 'error');
        });
    }
  };

  const handleClose = () => {
    if (doClose) {
      doClose()
        .then(
          (resolve) => {
            if (resolve.success) { // close
              close({ dataChanged: false, action: 'cancel' });
            }
          }
        )
        .catch((e) => console.error(e));
    }
  };

  const handleActivate = () => {
    if (doActivate) {
      doActivate()
        .then((result) => {
          if (result.success) {
            const msg = t('messages.activateRecord');
            showNotification(msg, 'success');
            doRefresh();
          } else if (result.error) {
            const msg = t('error.activateRecord') + result.error;
            showNotification(msg, 'error');
          }
        })
        .catch((resp) => {
          showNotification(resp.error, 'error');
        });
    }
  };

  const handleDeactivate = () => {
    if (doDeactivate) {
      doDeactivate()
        .then((result) => {
          if (result.success) {
            const msg = t('messages.deactivateRecord');
            showNotification(msg, 'success');
            close({ dataChanged: true, action: 'update' });
          } else if (result.error) {
            const msg = t('error.deactivateRecord') + result.error;
            showNotification(msg, 'error');
          }
        })
        .catch((resp) => {
          showNotification(resp.error, 'error');
        });
    }
  };

  const close = (result: ClosingDetails) => {
    onClose(result);
    hideDialog(level);
  };

  // const handleDelete = () => {
  //   if (doDelete) {
  //     doDelete().then((result) => {
  //       if (result.success) {
  //         const msg = t('messages.deleteRecord');
  //         showNotification(msg, 'success');
  //         close({ dataChanged: true, action: 'update' });
  //       } else if (result.error) {
  //         const msg = t('error.delete') + result.error;
  //         showNotification(msg, 'error');
  //       }
  //     });
  //   }
  // };

  let cmode = mode;
  if (record.status === 30 || (allowPrimaryModelActivate && record.active === false)) {
    cmode = 'view';
  }
  return (
    <DynamicDialog open maxWidth="sm" onClose={handleClose}>
      <DialogTitle>
        <Toolbar variant="dense" disableGutters>
          <DialogToolbarHeading>{title}</DialogToolbarHeading>
          <ToolbarFillContent />
          {/* {mode === 'update' || (mode === 'view' && record.status !== 40) ? (
            <DialogToolbarMenuButton>
              {record.status === 20 ? (
                <DialogToolbarMenuItem
                  onClick={handleDeactivate}
                  label={t('buttons.deactivate')}
                  icon={<CancelIcon color="action" />}
                />
              ) : null}
              {record.status === 30 ? (
                <DialogToolbarMenuItem
                  onClick={handleActivate}
                  label={t('buttons.activate')}
                  icon={<CheckCircleIcon color="action" />}
                />
              ) : null}

              <DialogToolbarMenuItem
                onClick={handleDelete}
                label={t('buttons.delete')}
                icon={<DeleteForeverIcon color="action" />}
              />
            </DialogToolbarMenuButton>
          ) : null} */}

          {allowPrimaryModelActivate && (
            <DialogToolbarMenuButton>
              {record.active ? (
                <DialogToolbarMenuItemDeactivate onClick={handleDeactivate} />
              ) : (
                <DialogToolbarMenuItemActivate onClick={handleActivate} />
              )}
            </DialogToolbarMenuButton>
          )}
          <DialogToolbarButtonClose onClick={handleClose} />
        </Toolbar>
      </DialogTitle>
      <DialogContent>
        <GridContainer my={2}>
          <FormContent
            fieldNames={dc.getFormFieldsNames(form || mode)}
            record={record}
            validation={validation}
            fields={fields}
            onFieldChange={handleFieldChange}
            mode={cmode}
            columns={columns}
            pickerDataControllers={pickerDataControllers}
            geometryChangeType={geometryChangeType}
            mapId={mapId}
          />
        </GridContainer>
      </DialogContent>
      <DialogActions>
        <Box sx={{ flexGrow: 1 }}>
          {mode === 'update' && allowPrimaryModelActivate && !record.active ? (
            <DialogInfoDeactivated />
          ) : (
            <DialogInfoMeta record={record} />
          )}
        </Box>
        <DialogActionButtonClose variant="outlined" onClick={handleClose} />
        {mode === 'insert' ? (
          <DialogActionButtonAdd variant="contained" onClick={handleInsert} />
        ) : mode === 'update' && (!allowPrimaryModelActivate || record.active === true) ? (
          <DialogActionButtonSave variant="contained" onClick={handleUpdate} />
        ) : null}
      </DialogActions>
    </DynamicDialog>
  );
};

export default GeneralDialog;
